import { db, auth } from '../services/firebaseConfig';
import {
  collection, doc, getDoc, getDocs, query, where, limit, orderBy,
  onSnapshot, serverTimestamp, setDoc, writeBatch, increment,
  startAfter
} from 'firebase/firestore';
import { getCountFromServer } from 'firebase/firestore';
import type { Store } from '../types/store';
import { secureLog } from '../lib/errors/logger';
import { sanitizeError } from '../utils/sanitization';
import { validateString, isValidDocumentId } from '../utils/validation';
import { verifyUserIdOwnership } from '../lib/security/idorProtection';

// Support multiple possible collection names for compatibility
const storesColUpper = collection(db, 'Stores');
const storesColLower = collection(db, 'stores');
const sellersColUpper = collection(db, 'Sellers');
const sellersColLower = collection(db, 'sellers');
const allStoreCols = [storesColUpper, storesColLower, sellersColUpper, sellersColLower];

const pickFirstDoc = async (promises: Promise<any>[]) => {
  const results = await Promise.allSettled(promises);
  for (const r of results) {
    if (r.status === 'fulfilled' && r.value && r.value.exists && r.value.exists()) return r.value;
  }
  return null;
};

export async function getStoreById(id: string): Promise<Store | null> {
  // Security: Input validation
  if (!isValidDocumentId(id)) {
    return null;
  }

  try {
    const snap = await pickFirstDoc(allStoreCols.map(col => getDoc(doc(col as any, id)) as any));
    return snap && snap.exists() ? ({ id: snap.id, ...(snap.data() as any) } as Store) : null;
  } catch (error) {
    secureLog('warn', 'Failed to get store by ID:', sanitizeError(error));
    return null;
  }
}

export async function getStoreBySlug(slug: string): Promise<Store | null> {
  // Security: Input validation
  const slugValidation = validateString(slug, 'Slug', 1, 200);
  if (!slugValidation.valid) {
    return null;
  }

  for (const col of allStoreCols) {
    try {
      const qRef = query(col, where('slug', '==', slug), limit(1));
      const snap = await getDocs(qRef);
      if (!snap.empty) {
        const d = snap.docs[0];
        return { id: d.id, ...(d.data() as any) } as Store;
      }
    } catch (error) {
      secureLog('warn', 'Failed to get store by slug:', sanitizeError(error));
    }
  }
  return null;
}

export function watchStore(id: string, cb: (s: Store | null) => void) {
  // Security: Input validation
  if (!isValidDocumentId(id)) {
    cb(null);
    return () => {}; // Return no-op unsubscribe
  }

  const unsubs = allStoreCols.map(col => onSnapshot(doc(col as any, id), (snap) => {
    if (snap.exists()) cb({ id: snap.id, ...(snap.data() as any) } as Store);
  }, (error) => {
    secureLog('warn', 'Error watching store:', sanitizeError(error));
  }));
  return () => unsubs.forEach(u => u());
}

export async function getStoreByOwner(ownerId: string): Promise<Store | null> {
  // Security: Input validation
  if (!isValidDocumentId(ownerId)) {
    return null;
  }

  // Security: IDOR Protection - Verify ownerId matches authenticated user (if authenticated)
  const currentUser = auth.currentUser;
  if (currentUser && ownerId !== currentUser.uid) {
    // SECURITY: Prevent IDOR - users can only query their own stores
    secureLog('warn', 'IDOR attempt detected in getStoreByOwner');
    return null;
  }

  for (const col of allStoreCols) {
    try {
      const qRef = query(col, where('ownerId', '==', ownerId), limit(1));
      const snap = await getDocs(qRef);
      if (!snap.empty) {
        const d = snap.docs[0];
        return { id: d.id, ...(d.data() as any) } as Store;
      }
    } catch (error) {
      secureLog('warn', 'Failed to get store by owner:', sanitizeError(error));
    }
  }
  return null;
}

export async function followStore(storeId: string, userId: string) {
  // Security: Input validation
  if (!isValidDocumentId(storeId) || !isValidDocumentId(userId)) {
    throw new Error('Invalid store or user ID');
  }

  // Security: IDOR Protection - Verify userId matches authenticated user
  try {
    verifyUserIdOwnership(userId, {
      action: 'follow store',
      resourceType: 'store followers'
    });
  } catch (error: any) {
    throw new Error(error.message || 'Access denied');
  }

  try {
    const batch = writeBatch(db);
    const followerRef = doc(db, 'Stores', storeId, 'followers', userId);
    batch.set(followerRef, { createdAt: serverTimestamp() }, { merge: true });
    batch.set(doc(storesColUpper, storeId), { followersCount: increment(1), updatedAt: serverTimestamp() }, { merge: true });
    await batch.commit();
  } catch (error) {
    secureLog('warn', 'Failed to follow store:', sanitizeError(error));
    throw error;
  }
}

export async function unfollowStore(storeId: string, userId: string) {
  // Security: Input validation
  if (!isValidDocumentId(storeId) || !isValidDocumentId(userId)) {
    throw new Error('Invalid store or user ID');
  }

  // Security: IDOR Protection - Verify userId matches authenticated user
  try {
    verifyUserIdOwnership(userId, {
      action: 'unfollow store',
      resourceType: 'store followers'
    });
  } catch (error: any) {
    throw new Error(error.message || 'Access denied');
  }

  try {
    const batch = writeBatch(db);
    batch.delete(doc(db, 'Stores', storeId, 'followers', userId));
    batch.set(doc(storesColUpper, storeId), { followersCount: increment(-1), updatedAt: serverTimestamp() }, { merge: true });
    await batch.commit();
  } catch (error) {
    secureLog('warn', 'Failed to unfollow store:', sanitizeError(error));
    throw error;
  }
}

export async function isFollowingStore(storeId: string, userId: string): Promise<boolean> {
  // Security: Input validation
  if (!isValidDocumentId(storeId) || !isValidDocumentId(userId)) {
    return false;
  }

  // Security: IDOR Protection - Verify userId matches authenticated user
  try {
    verifyUserIdOwnership(userId, {
      action: 'check store following status',
      resourceType: 'store followers'
    });
  } catch (error: any) {
    // Return false instead of throwing for read operations
    return false;
  }

  try {
    const snap = await getDoc(doc(db, 'Stores', storeId, 'followers', userId));
    return snap.exists();
  } catch (error) {
    secureLog('warn', 'Failed to check if following store:', sanitizeError(error));
    return false;
  }
}

export async function reportStore(storeId: string, reporterId: string, reason: string, details?: string) {
  // Security: Input validation
  if (!isValidDocumentId(storeId) || !isValidDocumentId(reporterId)) {
    throw new Error('Invalid store or reporter ID');
  }

  const reasonValidation = validateString(reason, 'Reason', 1, 500);
  if (!reasonValidation.valid) {
    throw new Error(reasonValidation.message || 'Invalid reason');
  }

  // Security: IDOR Protection - Verify reporterId matches authenticated user
  const currentUser = auth.currentUser;
  if (!currentUser || reporterId !== currentUser.uid) {
    secureLog('warn', 'IDOR attempt detected in reportStore');
    throw new Error('Access denied');
  }

  // Security: Sanitize details
  const sanitizedDetails = details ? details.slice(0, 1000) : '';

  try {
    const reportRef = doc(collection(db, 'StoreReports'));
    await setDoc(reportRef, { 
      storeId, 
      reporterId: currentUser.uid, // Ensure reporterId matches authenticated user
      reason: reason.slice(0, 500), 
      details: sanitizedDetails, 
      createdAt: serverTimestamp() 
    });
  } catch (error) {
    secureLog('warn', 'Failed to report store:', sanitizeError(error));
    throw error;
  }
}

export type StoresQuery = {
  search?: string;
  sortBy?: 'latest'|'followers'|'products';
  pageSize?: number;
  cursor?: any;
};

export async function listStores(qs: StoresQuery) {
  const { search, sortBy = 'latest', pageSize = 24, cursor } = qs || {};
  
  // Security: Input validation
  const validPageSize = Math.min(Math.max(1, Math.floor(pageSize) || 24), 100); // Max 100 items per page
  const validSortBy = ['latest', 'followers', 'products'].includes(sortBy) ? sortBy : 'latest';
  
  // Security: Validate search query
  const sanitizedSearch = search && typeof search === 'string' ? search.trim().slice(0, 100) : undefined;

  try {
    // Aggregate from multiple collections. Pagination will be per-collection best-effort.
    const col = storesColUpper; // main; others appended
    let qRef: any = col;
    switch (validSortBy) {
      case 'followers': qRef = query(qRef, orderBy('followersCount', 'desc')); break;
      case 'products': qRef = query(qRef, orderBy('productsCount', 'desc')); break;
      default: qRef = query(qRef, orderBy('createdAt', 'desc')); break;
    }
    qRef = query(qRef, limit(validPageSize));
    if (cursor) qRef = query(qRef, startAfter(cursor));
    const snap = await getDocs(qRef);
    let items: Store[] = snap.docs.map((d:any)=>({ id:d.id, ...(d.data() as any) }) as Store);
    const nextCursor: any = snap.docs.length ? snap.docs[snap.docs.length-1] : null;
    if (sanitizedSearch) {
      const s = sanitizedSearch.toLowerCase();
      items = items.filter(it => (it.name || '').toLowerCase().includes(s) || (it.slug || '').toLowerCase().includes(s));
    }
    return { items, nextCursor };
  } catch (error) {
    secureLog('warn', 'Failed to list stores:', sanitizeError(error));
    return { items: [], nextCursor: null };
  }
}

export async function getStoreStats(store: Store | string) {
  const storeId = typeof store === 'string' ? store : store.id;
  const sellerId = typeof store === 'string' ? store : (store.ownerId || store.id);
  
  // Security: Input validation
  if (!isValidDocumentId(storeId) || !isValidDocumentId(sellerId)) {
    return {
      productsCount: 0,
      reviewsTotal: 0,
      positiveCount: 0,
      negativeCount: 0,
    };
  }

  try {
    // Products count
    const productsCountSnap = await getCountFromServer(query(collection(db, 'Products'), where('sellerId', '==', sellerId)));
    // Reviews counts (global collection)
    const allReviews = await getCountFromServer(query(collection(db, 'StoreReviews'), where('storeId', '==', storeId)));
    const positive = await getCountFromServer(query(collection(db, 'StoreReviews'), where('storeId', '==', storeId), where('rating', '>=', 4)));
    const negative = await getCountFromServer(query(collection(db, 'StoreReviews'), where('storeId', '==', storeId), where('rating', '<=', 2)));
    return {
      productsCount: productsCountSnap.data().count || 0,
      reviewsTotal: allReviews.data().count || 0,
      positiveCount: positive.data().count || 0,
      negativeCount: negative.data().count || 0,
    };
  } catch (error) {
    secureLog('warn', 'Failed to get store stats:', sanitizeError(error));
    return {
      productsCount: 0,
      reviewsTotal: 0,
      positiveCount: 0,
      negativeCount: 0,
    };
  }
}


